import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getRecommendStocks } from "@/api/market";
import type { StockItem, WatchlistEntry } from "@/api/market";
import { useRealtimeWatchlist } from "@/hooks/useRealtimeWatchlist";

// Strip sh/sz/bj prefix from a ticker code
function stripPrefix(code: string): string {
  const c = code.trim().toLowerCase();
  return (c.startsWith("sh") || c.startsWith("sz") || c.startsWith("bj")) ? c.slice(2) : c;
}

function ratingFromScore(score?: number): { label: string; color: string } {
  if (!score) return { label: "--", color: "#64748B" };
  if (score >= 0.85) return { label: "超配", color: "#E11D48" };
  if (score >= 0.75) return { label: "增持", color: "#F97316" };
  if (score >= 0.65) return { label: "中性", color: "#64748B" };
  return { label: "关注", color: "#3B82F6" };
}

export default function Watchlist({ sessionId }: { sessionId?: number }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"ai" | "user">("ai");
  const { watchlist: userWatchlist, loading: userWlLoading } = useRealtimeWatchlist();
  const [aiStocks, setAiStocks] = useState<StockItem[]>([]);
  const [aiQuotes, setAiQuotes] = useState<Record<string, any>>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // AI自选: 初始加载 + 每秒刷新价格
  useEffect(() => {
    if (tab !== "ai") return;
    if (!sessionId) {
      setAiStocks([]);
      setAiError("");
      return;
    }
    setAiLoading(true);
    setAiError("");

    const fetchAi = () => {
      if (document.visibilityState !== 'visible') return Promise.resolve();
      const codes = userWatchlist.map((w) => stripPrefix(w.code)).join(",");
      return getRecommendStocks(sessionId, codes || undefined)
        .then((res) => {
          const stocks = res.stocks || [];
          setAiStocks(stocks);
          const m: Record<string, any> = {};
          stocks.forEach((s: any) => {
            const code = s.code || s.stockCode || "";
            if (code) m[code] = { price: s.price, changePct: s.changePct };
          });
          setAiQuotes(m);
          setAiError("");
        })
        .catch((e) => {
          setAiError(e instanceof Error ? e.message : "加载失败");
          setAiStocks([]);
        });
    };

    fetchAi().finally(() => setAiLoading(false));

    const timer = setInterval(fetchAi, 1000);
    document.addEventListener('visibilitychange', fetchAi);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', fetchAi);
    };
  }, [tab, sessionId, userWatchlist]);

  const renderCard = (name: string, code: string, reason: string, q: any, tagLabel: string, tagColor: string) => {
    const loaded = q && q.price !== undefined;
    return (
      <div key={code} className="flex items-center justify-between px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground-950">{name}</span>
            <span className="text-[10px] text-foreground-400">{stripPrefix(code)}</span>
          </div>
          <div className="text-[10px] text-foreground-500 mt-0.5 truncate">{reason}</div>
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          <div className="text-xs font-semibold text-foreground-950">{loaded ? `¥${q.price.toFixed(2)}` : "--"}</div>
          <div className="text-[10px] font-medium">
            {loaded ? (
              <span className={(q.changePct ?? 0) >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}>
                {(q.changePct ?? 0) >= 0 ? "+" : ""}{(q.changePct ?? 0).toFixed(2)}%
              </span>
            ) : (
              <span className="text-foreground-300">--</span>
            )}
          </div>
        </div>
        <div className="ml-3 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap" style={{ color: tagColor, background: `${tagColor}15` }}>
          {loaded ? tagLabel : "--"}
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-foreground-950">自选</span>
        <button onClick={() => navigate("/watchlist")} className="text-xs text-primary-500 whitespace-nowrap cursor-pointer">查看更多 →</button>
      </div>
      <div className="flex gap-2 mb-2">
        <button onClick={() => setTab("ai")}
          className={`text-[11px] px-3 py-1 rounded-full font-medium transition-colors cursor-pointer ${tab === "ai" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>AI自选</button>
        <button onClick={() => setTab("user")}
          className={`text-[11px] px-3 py-1 rounded-full font-medium transition-colors cursor-pointer ${tab === "user" ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>我的自选</button>
      </div>
      <div className="bg-white rounded-card border border-background-200/60 overflow-hidden">
        {tab === "ai" ? (
          aiLoading ? (
            <div className="p-6 text-center">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-foreground-400 mt-2">AI 正在扫描市场...</p>
            </div>
          ) : aiError ? (
            <div className="p-6 text-center">
              <i className="ri-cloud-off-line text-xl text-foreground-300" />
              <p className="text-xs text-foreground-400 mt-1">{aiError}</p>
            </div>
          ) : !sessionId ? (
            <div className="p-6 text-center">
              <i className="ri-rocket-2-line text-xl text-foreground-300" />
              <p className="text-xs text-foreground-400 mt-1">请先开启模拟盘</p>
            </div>
          ) : aiStocks.length === 0 ? (
            <div className="p-6 text-center">
              <i className="ri-search-eye-line text-xl text-foreground-300" />
              <p className="text-xs text-foreground-400 mt-1">暂无推荐标的</p>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {aiStocks.map((s) => {
                const q = aiQuotes[s.code];
                const { label, color } = ratingFromScore(s.matchScore);
                return renderCard(s.name, s.code, s.reason || "—", q, label, color);
              })}
            </div>
          )
        ) : userWlLoading ? (
          <div className="p-6 text-center">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs text-foreground-400 mt-2">加载中...</p>
          </div>
        ) : userWatchlist.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-star-line text-2xl text-foreground-300" />
            <p className="text-xs text-foreground-400 mt-2">还没有自选股</p>
            <p className="text-[10px] text-foreground-300 mt-1">去「查看更多」添加</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {userWatchlist.map((w) => {
              const q = { price: w.currentPrice, changePct: w.todayChangePct, totalCap: undefined };
              const change = w.todayChangePct;
              return renderCard(
                w.name, w.code,
                w.industry ? `${w.industry} · 自加入${w.changePct >= 0 ? "+" : ""}${w.changePct.toFixed(1)}%` : `自加入${w.changePct >= 0 ? "+" : ""}${w.changePct.toFixed(1)}%`,
                q,
                change >= 0 ? "关注" : "审慎",
                change >= 0 ? "#E11D48" : "#10B981"
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}